import { useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { SERVICES, PUNE_AREAS, BRAND } from "@/lib/site-data";

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    location: "",
    service: "",
    message: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.location || !form.service) {
      toast.error("Please fill in name, phone, location and service.");
      return;
    }
    setLoading(true);
    try {
      const lines = [
        "🦷 *New Home-Visit Booking*",
        "",
        `*Name:* ${form.name}`,
        `*Phone:* ${form.phone}`,
      ];
      if (form.email) lines.push(`*Email:* ${form.email}`);
      lines.push(`*Area:* ${form.location}`);
      lines.push(`*Service:* ${form.service}`);
      if (date) lines.push(`*Preferred date:* ${format(date, "PPP")}`);
      if (form.message) lines.push(`*Note:* ${form.message}`);

      const waUrl = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
        lines.join("\n")
      )}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");

      toast.success("Opening WhatsApp — please send the prefilled message to confirm your booking.");
      setForm({ name: "", phone: "", email: "", location: "", service: "", message: "" });
      setDate(null);
    } catch (err) {
      toast.error("Something went wrong. Please call us on " + BRAND.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="py-20 md:py-28 bg-brand-pine text-white relative overflow-hidden" data-testid="booking-section">
      <div aria-hidden className="absolute inset-0 opacity-[0.07]"
        style={{ backgroundImage: "radial-gradient(circle at 10% 10%, #FAF9F6 0, transparent 40%), radial-gradient(circle at 90% 90%, #C86A53 0, transparent 45%)" }}
      />
      <div className="container-site relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-brand-clay mb-3">Book a home visit</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Care that reaches <span className="italic text-brand-clay">your doorstep.</span>
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed max-w-md">
              Share a few details and our team calls you back — usually within
              the hour. No pressure, no hidden fees.
            </p>
            <div className="mt-8 space-y-3 text-white/80 text-sm">
              <p>📞 {BRAND.phone} · Mon–Sun, 9 AM – 9 PM</p>
              <p>💬 WhatsApp for quick tele-consults</p>
              <p>📍 All Pune + Pimpri-Chinchwad</p>
            </div>
          </div>

          <form onSubmit={submit} data-testid="booking-form" className="lg:col-span-7 bg-brand-bg text-brand-text rounded-3xl p-7 md:p-10 shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="name">Full name*</Label>
                <Input
                  id="name"
                  data-testid="input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="e.g. Priya Deshpande"
                  className="h-12 rounded-xl bg-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone number*</Label>
                <Input
                  id="phone"
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 98xxxxxxxx"
                  className="h-12 rounded-xl bg-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email (optional)</Label>
                <Input
                  id="email"
                  data-testid="input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  className="h-12 rounded-xl bg-white"
                />
              </div>
              <div className="space-y-2">
                <Label>Area in Pune*</Label>
                <Select value={form.location} onValueChange={update("location")}>
                  <SelectTrigger className="h-12 rounded-xl bg-white" data-testid="select-location">
                    <SelectValue placeholder="Select your area" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {PUNE_AREAS.map((a) => (
                      <SelectItem key={a} value={a}>
                        {a}
                      </SelectItem>
                    ))}
                    <SelectItem value="Other">Other (mention in message)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Service needed*</Label>
                <Select value={form.service} onValueChange={update("service")}>
                  <SelectTrigger className="h-12 rounded-xl bg-white" data-testid="select-service">
                    <SelectValue placeholder="What do you need?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General Checkup">General Checkup</SelectItem>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.slug} value={s.title}>
                        {s.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="Other / Not sure">Other / Not sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Preferred date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="date-picker-trigger"
                      className={cn(
                        "h-12 w-full rounded-xl bg-white border border-input px-3 inline-flex items-center gap-2 text-left",
                        !date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon size={16} />
                      {date ? format(date, "PPP") : "Pick a date"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="message">Anything we should know?</Label>
                <Textarea
                  id="message"
                  data-testid="input-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Patient age, mobility, medical conditions, etc."
                  className="rounded-xl bg-white min-h-[96px]"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="booking-submit-btn"
              className="btn-primary mt-7 w-full md:w-auto"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {loading ? "Sending..." : "Request a home visit"}
            </button>
            <p className="text-xs text-brand-textMuted mt-4">
              By submitting, you consent to being contacted by our care team on the number provided.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
